import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogMain,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@ncoder/ui';
import { DialogActions, FormField, ORG_ADMIN_INPUT_CLASS } from './OrgAdminForm';
import { formatCurrencyUsd } from '@/components/organisms/org-admin/usageFormat';
import type { SetMemberBudgetInput } from '@/services/org-admin';
import type { MemberAllowance } from '@/services/org-admin/allowance';

interface BudgetFormValues {
  amount_usd: string;
}

export interface MemberBudgetEditModalTarget {
  user_id: string;
  email: string | null;
  budget_usd: number;
  /**
   * The current split (#327). `PUT …/budget` sets the PLAN part only and the backend adds the
   * member's credits back on top, so the field is seeded with the plan figure — seeding it with the
   * total would make "save without changing anything" silently raise the allowance by the credits.
   */
  allowance: MemberAllowance | null;
}

export interface MemberBudgetEditModalProps {
  open: boolean;
  member: MemberBudgetEditModalTarget | null;
  isSubmitting: boolean;
  onSubmit: (data: SetMemberBudgetInput) => void;
  onClose: () => void;
}

export function MemberBudgetEditModal({
  open,
  member,
  isSubmitting,
  onSubmit,
  onClose,
}: MemberBudgetEditModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<BudgetFormValues>({
    defaultValues: { amount_usd: '' },
    mode: 'onChange',
  });

  useEffect(() => {
    if (!open || !member) return;
    reset({ amount_usd: String(member.allowance?.planBudgetUsd ?? member.budget_usd) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, member, reset]);

  const credits = member?.allowance?.creditBalanceUsd ?? null;

  const submit = handleSubmit((data) => {
    onSubmit({ amount_usd: Number(data.amount_usd) });
  });

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">Set plan allowance</DialogTitle>
          <DialogDescription className="text-[13px]">
            {/* The seat this applies to is the single most important fact in this dialog, so it is
                the subject of the sentence rather than a prefix before a paragraph of mechanism. */}
            <span className="font-medium text-foreground">{member?.email ?? member?.user_id}</span>
            {member && credits !== null && member.allowance?.planBudgetUsd !== null && (
              <>
                {' '}
                — currently {formatCurrencyUsd(member.allowance!.planBudgetUsd!)} from the plan plus{' '}
                {formatCurrencyUsd(credits)} of credit, {formatCurrencyUsd(member.budget_usd)} in total.
              </>
            )}
            {member && (credits === null || member.allowance?.planBudgetUsd === null) && (
              <>
                {' '}
                — currently {formatCurrencyUsd(member.budget_usd)} per cycle.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col gap-5" noValidate>
          <DialogMain className="space-y-4">
            <FormField
              label="Plan allowance (USD)"
              required
              hint={
                credits !== null && credits > 0
                  ? `Credit the member holds (${formatCurrencyUsd(credits)}) is added on top and is not changed here. Once plan plus credit is spent, requests pause until the plan part resets.`
                  : 'The plan part of the allowance, which resets each cycle. Any credit the member holds is added on top. Once it is all spent, requests pause until the reset.'
              }
              error={errors.amount_usd?.message}
            >
              {(fieldProps) => (
                <input
                  {...fieldProps}
                  type="number"
                  step="0.01"
                  min="0"
                  inputMode="decimal"
                  autoFocus
                  placeholder="10.00"
                  disabled={isSubmitting}
                  className={ORG_ADMIN_INPUT_CLASS}
                  {...register('amount_usd', {
                    required: 'Enter an amount',
                    validate: (value) => {
                      const amount = Number(value);
                      if (Number.isNaN(amount)) return 'Enter a number';
                      if (amount < 0) return 'A budget cannot be negative';
                      return true;
                    },
                  })}
                />
              )}
            </FormField>
          </DialogMain>

          <DialogFooter>
            <DialogActions
              onCancel={onClose}
              submitLabel="Save allowance"
              pendingLabel="Saving…"
              isSubmitting={isSubmitting}
              isDisabled={!isValid}
            />
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
